// Wall-clock times in IANA zones, without a date library.
// Flights are stored as local times at each airport plus the airport's zone; these helpers
// turn them into instants (for sorting, durations) and back into readable labels.

import type { LocalDateTime } from './types.ts';

// Building an Intl.DateTimeFormat is slow (a fraction of a millisecond, several times that on a
// phone) and sorting 100+ flights converts thousands of times, so keep one per zone.
const formatters = new Map<string, Intl.DateTimeFormat>();
const validZones = new Map<string, boolean>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let fmt = formatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, fmt);
  }
  return fmt;
}

function partsInZone(instant: Date, timeZone: string): Record<string, number> {
  const fmt = formatterFor(timeZone);
  const out: Record<string, number> = {};
  for (const p of fmt.formatToParts(instant)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return out;
}

/** Offset of `timeZone` from UTC at `instant`, in minutes (e.g. -240 for New York in summer). */
export function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const p = partsInZone(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

export function isValidTimeZone(tz: string | undefined): tz is string {
  if (!tz) return false;
  let ok = validZones.get(tz);
  if (ok === undefined) {
    try {
      formatterFor(tz);
      ok = true;
    } catch {
      ok = false;
    }
    validZones.set(tz, ok);
  }
  return ok;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isValidDate(s: string | undefined): s is string {
  if (!s || !DATE_RE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

export function isValidTime(s: string | undefined): s is string {
  return !!s && TIME_RE.test(s);
}

/**
 * The instant a local wall-clock time refers to. Missing time = midday (stable sort position);
 * missing/invalid zone = UTC.
 */
export function toInstant(ldt: LocalDateTime): Date {
  const [y, m, d] = ldt.date.split('-').map(Number);
  const [hh, mm] = (ldt.time ?? '12:00').split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  if (!isValidTimeZone(ldt.timeZone)) return new Date(guess);
  // Two passes handle DST boundaries.
  let instant = guess - zoneOffsetMinutes(new Date(guess), ldt.timeZone) * 60_000;
  instant = guess - zoneOffsetMinutes(new Date(instant), ldt.timeZone) * 60_000;
  return new Date(instant);
}

/** "7:40 PM" (in the place's own zone; the stored time already is local). */
export function formatClock(time: string | undefined): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Day of week for a plain date, independent of the viewer's zone. */
function weekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** "Mon, Nov 3" */
export function formatDay(date: string): string {
  const [, m, d] = date.split('-').map(Number);
  return `${WEEKDAYS[weekday(date)]}, ${MONTHS[m - 1]} ${d}`;
}

/** "Nov 3" */
export function formatShortDate(date: string): string {
  const [, m, d] = date.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

/** "Nov 3 – 9, 2026" or "Oct 30 – Nov 2, 2026" */
export function formatRange(start: string, end: string): string {
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  if (ys === ye && ms === me) return `${MONTHS[ms - 1]} ${ds} – ${de}, ${ye}`;
  if (ys === ye) return `${MONTHS[ms - 1]} ${ds} – ${MONTHS[me - 1]} ${de}, ${ye}`;
  return `${MONTHS[ms - 1]} ${ds}, ${ys} – ${MONTHS[me - 1]} ${de}, ${ye}`;
}

export function monthAbbrev(date: string): string {
  return MONTHS[Number(date.split('-')[1]) - 1].toUpperCase();
}

export function dayOfMonth(date: string): number {
  return Number(date.split('-')[2]);
}

/** "6 h 15 m" between two local times in (possibly) different zones. */
export function formatDuration(start: LocalDateTime, end: LocalDateTime): string {
  const mins = Math.round((toInstant(end).getTime() - toInstant(start).getTime()) / 60_000);
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} m`;
}

/** Whole days from date a to date b (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((dateToUtcMs(b) - dateToUtcMs(a)) / 86_400_000);
}

function dateToUtcMs(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Add whole days to a YYYY-MM-DD date. */
export function addDays(date: string, days: number): string {
  return new Date(dateToUtcMs(date) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Today's date (YYYY-MM-DD) in a zone; defaults to the device zone. */
export function todayIn(timeZone?: string, now: Date = new Date()): string {
  const tz = isValidTimeZone(timeZone) ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const p = partsInZone(now, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Friendly zone label: "Lisbon time" from "Europe/Lisbon". */
export function zoneLabel(timeZone: string | undefined): string {
  if (!timeZone) return '';
  const city = timeZone.split('/').pop()!.replace(/_/g, ' ');
  return `${city} time`;
}
